import * as RT from '@radix-ui/react-toast';
import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { classNames } from '@/lib/format';

export type ToastTone = 'success' | 'danger' | 'warning' | 'info';

interface ToastItem {
  id: string;
  title: string;
  description?: string;
  tone: ToastTone;
}

interface ToastContextValue {
  push: (t: Omit<ToastItem, 'id'>) => void;
}

const ToastContext = createContext<ToastContextValue | undefined>(undefined);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const push = useCallback((t: Omit<ToastItem, 'id'>) => {
    setItems((cur) => [...cur, { ...t, id: Math.random().toString(36).slice(2) }]);
  }, []);
  const dismiss = useCallback((id: string) => {
    setItems((cur) => cur.filter((i) => i.id !== id));
  }, []);
  const value = useMemo(() => ({ push }), [push]);
  return (
    <ToastContext.Provider value={value}>
      <RT.Provider duration={4000} swipeDirection="right">
        {children}
        {items.map((i) => (
          <RT.Root
            key={i.id}
            className={classNames('toast', `toast--${i.tone}`)}
            onOpenChange={(open) => {
              if (!open) dismiss(i.id);
            }}
          >
            <div>
              <RT.Title className="toast__title">{i.title}</RT.Title>
              {i.description && <RT.Description>{i.description}</RT.Description>}
            </div>
            <RT.Close className="toast__close" aria-label="Close">
              ✕
            </RT.Close>
          </RT.Root>
        ))}
        <RT.Viewport className="toast__viewport" />
      </RT.Provider>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within ToastProvider');
  return ctx;
}
